import React from "react";
import { useNavigate } from "react-router-dom";
import { LocationDetail, useCustomer } from "../../contexts/CustomerContext";
import LoyaltyModal from "./LoyaltyModal";
import SallyLogo from "../../assets/logo.svg";
import styles from "./Portal.module.scss";
import LoyaltyChips from "./LoyaltyChips";
import { unsubscribeFromLocation } from "../../api";
import { rewardsReadyCount } from "../../utils/loyaltyBusiness";

interface LocationCardProps {
  location: LocationDetail;
  onHide?: () => void;
  onUnhide?: () => void;
  hidden?: boolean;
  /** False when the location's loyalty is shown on its business card instead. */
  showLoyalty?: boolean;
}

const LocationCard: React.FC<LocationCardProps> = ({
  location,
  onHide,
  onUnhide,
  hidden = false,
  showLoyalty = true,
}) => {
  const [imgError, setImgError] = React.useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = React.useState(false);
  const [unsubscribing, setUnsubscribing] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { refreshProfile } = useCustomer();

  // Close menu when clicking outside
  React.useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleDevUnsubscribe = async () => {
    if (!window.confirm(`[DEV] Remove yourself from "${location.name}"?`))
      return;
    setUnsubscribing(true);
    try {
      await unsubscribeFromLocation(location.id);
      refreshProfile();
    } catch (e: any) {
      alert(`Unsubscribe failed: ${e?.message}`);
    } finally {
      setUnsubscribing(false);
    }
  };

  const orderingEnabled = location.orderingEnabled;
  const loyalty = showLoyalty ? location.loyalty : null;
  const hasPunchCards = showLoyalty && (location.punchCards ?? []).length > 0;

  return (
    <>
      <div className={styles.Card}>
        <div className={styles.CardTop}>
          {location.logoURL && !imgError ? (
            <img
              src={location.logoURL}
              alt={location.name}
              className={styles.CardLogo}
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={styles.CardLogoPlaceholder}>
              <img
                src={SallyLogo}
                alt="Sally"
                className={styles.CardLogoFallback}
              />
            </div>
          )}

          <div className={styles.CardBody}>
            <p className={styles.CardName}>{location.name}</p>
            {location.city && (
              <p className={styles.CardCity}>{location.city}</p>
            )}
            {showLoyalty && (
              <LoyaltyChips
                loyalty={loyalty}
                rewardsReady={rewardsReadyCount(location)}
              />
            )}
          </div>

          {/* ⋯ menu */}
          <div className={styles.CardMenu} ref={menuRef}>
            <button
              className={styles.CardMenuBtn}
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="More options"
            >
              ···
            </button>
            {menuOpen && (
              <div className={styles.CardMenuDropdown}>
                {hidden ? (
                  <button
                    className={styles.CardMenuItem}
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onUnhide?.();
                    }}
                  >
                    Unhide venue
                  </button>
                ) : (
                  <button
                    className={styles.CardMenuItem}
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onHide?.();
                    }}
                  >
                    Hide venue
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={styles.CardActions}>
          {loyalty && (
            <button
              className={`${styles.CardAction} ${styles.CardActionSecondary}`}
              type="button"
              onClick={() => setLoyaltyOpen(true)}
            >
              Loyalty
            </button>
          )}
          {hasPunchCards && (
            <button
              className={`${styles.CardAction} ${styles.CardActionSecondary}`}
              type="button"
              onClick={() => navigate(`/location/${location.id}/punch-cards`)}
            >
              Punch cards
            </button>
          )}
          <button
            className={`${styles.CardAction} ${styles.CardActionSecondary}`}
            type="button"
            onClick={() => navigate(`/location/${location.id}/orders`)}
          >
            Past Orders
          </button>
          {orderingEnabled && (
            <button
              className={`${styles.CardAction} ${styles.CardActionPrimary}`}
              type="button"
              onClick={() => navigate(`/location/${location.id}`)}
            >
              Menu
            </button>
          )}
          {location.devMode && (
            <button
              className={`${styles.CardAction} ${styles.CardActionDev}`}
              type="button"
              onClick={handleDevUnsubscribe}
              disabled={unsubscribing}
            >
              {unsubscribing ? "Removing…" : "DEV: Unsubscribe"}
            </button>
          )}
        </div>
      </div>

      {loyalty && (
        <LoyaltyModal
          active={loyaltyOpen}
          locationId={location.id}
          locationName={location.name}
          loyalty={loyalty}
          currency={location.currency ?? ""}
          onClose={() => setLoyaltyOpen(false)}
        />
      )}
    </>
  );
};

export default LocationCard;
